import { useMutation } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { AuthLayout } from 'src/components/auth-layout'
import { Button } from 'src/components/button'
import { ErrorMessage, ErrorsList } from 'src/components/errors'
import { Input } from 'src/components/input'
import {
  EmailForgotPasswordForm,
  EmailForgotPasswordInput,
  TEmailForgotPasswordForm,
  userApi,
} from 'src/services/user'
import { getErrorMessages } from 'src/utils/errors'

export const RequestPasswordResetInner = () => {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState<string[] | undefined>()
  const [resetLinkSent, setResetLinkSent] = useState(false)
  const { createFormFieldChangeHandler, form } = useTnForm<TEmailForgotPasswordForm>()
  const navigate = useNavigate()

  const { mutate: requestReset } = useMutation({
    mutationFn: userApi.csc.requestPasswordReset,
    onSuccess: () => {
      setErrorMessage(undefined)
      setResetLinkSent(true)
    },
    onError(e: any) {
      const errors = getErrorMessages(e)
      setErrorMessage(errors)
    },
  })

  const handleRequest = () => {
    const input = {
      email: form.email.value ?? '',
    }
    requestReset(input)
  }

  return (
    <AuthLayout title={t('auth.requestPasswordResetTitle')}>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        {resetLinkSent ? (
          <>
            <p className="text-md">{t('auth.resetSubmitted')}</p>
            <p className="text-md">{t('auth.checkSpam')}</p>
            <div className="pt-6">
              <Button
                onClick={() => {
                  navigate('/log-in')
                }}
                variant="primary"
              >
                {t('auth.returnToLogin')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault()
              }}
              className="flex flex-col gap-2"
            >
              <Input
                placeholder={t('auth.emailPlaceholder')}
                onChange={(e) => createFormFieldChangeHandler(form.email)(e.target.value)}
                value={form.email.value ?? ''}
                data-cy="email"
                id="id"
                label={t('auth.emailLabel')}
              />
              <ErrorsList errors={form.email.errors} />
              <div className="mb-2">
                <ErrorMessage>{errorMessage}</ErrorMessage>
              </div>
              <Button onClick={handleRequest} disabled={!form.isValid} variant={'primary'}>
                {t('auth.requestPasswordResetTitle')}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  )
}

export const RequestPasswordReset = () => {
  return (
    <FormProvider<EmailForgotPasswordInput> formClass={EmailForgotPasswordForm}>
      <RequestPasswordResetInner />
    </FormProvider>
  )
}
